import type Highcharts from "highcharts";
import type { AiChatChartData } from "./types";
import { isSafeChartText } from "./chartPresentation";

const numeric = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  Math.abs(value) <= 1e15;
const label = (value: unknown): value is string =>
  typeof value === "string" &&
  value.length > 0 &&
  value.length <= 256 &&
  isSafeChartText(value);
const heatmapDate = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  timeZone: "UTC",
});
const heatmapTime = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: "UTC",
});

export function isDistributionChart(chart: AiChatChartData): boolean {
  if (
    chart.schemaVersion !== 1 ||
    chart.presentation != null ||
    !Array.isArray(chart.series) ||
    chart.series.length !== 0
  )
    return false;
  if (chart.title != null && !label(chart.title)) return false;
  if (
    [chart.xAxisTitle, chart.yAxisTitle].some(
      (value) => value != null && value !== "" && !label(value)
    )
  )
    return false;
  if (chart.chartType === "boxplot") {
    return (
      Array.isArray(chart.boxes) &&
      chart.boxes.length > 0 &&
      chart.boxes.length <= 6 &&
      chart.boxes.every(
        (box) =>
          !!box &&
          label(box.name) &&
          [box.low, box.q1, box.median, box.q3, box.high].every(numeric) &&
          box.low <= box.median &&
          box.median <= box.high &&
          box.q1 <= box.median &&
          box.median <= box.q3 &&
          Number.isInteger(box.sampleCount) &&
          box.sampleCount > 0 &&
          box.sampleCount <= 5000 &&
          Array.isArray(box.outliers) &&
          box.outliers.length <= box.sampleCount &&
          box.outliers.every(
            (value) => numeric(value) && (value < box.low || value > box.high)
          )
      )
    );
  }
  const matrix = chart.matrix;
  return (
    ["heatmap", "correlation", "polar"].includes(chart.chartType) &&
    !!matrix &&
    Array.isArray(matrix.rows) &&
    Array.isArray(matrix.columns) &&
    matrix.rows.length > 0 &&
    matrix.columns.length > 0 &&
    matrix.rows.length * matrix.columns.length <= 5000 &&
    matrix.rows.every(label) &&
    matrix.columns.every(label) &&
    typeof matrix.unit === "string" &&
    matrix.unit.length <= 256 &&
    isSafeChartText(matrix.unit) &&
    Array.isArray(matrix.values) &&
    matrix.values.length === matrix.rows.length &&
    matrix.values.every(
      (row) =>
        Array.isArray(row) &&
        row.length === matrix.columns.length &&
        row.every((value) => value === null || numeric(value))
    ) &&
    (chart.chartType !== "polar" ||
      (matrix.angleMetadataError == null &&
        matrix.columns.length === 1 &&
        matrix.values.some((row) => row[0] !== null) &&
        (matrix.rowAngles == null ||
          (Array.isArray(matrix.rowAngles) &&
            matrix.rowAngles.length === matrix.rows.length &&
            matrix.rowAngles.every(
              (angle) => numeric(angle) && angle >= 0 && angle < 360
            ) &&
            new Set(matrix.rowAngles).size === matrix.rows.length)))) &&
    (chart.chartType !== "correlation" ||
      (matrix.rows.length >= 2 &&
        matrix.rows.length <= 6 &&
        matrix.rows.length === matrix.columns.length &&
        Number.isInteger(matrix.alignedSampleCount) &&
        matrix.alignedSampleCount! >= 3 &&
        matrix.alignedSampleCount! <= 5000 &&
        matrix.rows.every((name, index) => name === matrix.columns[index]) &&
        matrix.values.every((row, rowIndex) =>
          row.every(
            (value, columnIndex) =>
              numeric(value) &&
              Math.abs(value) <= 1 &&
              value === matrix.values[columnIndex][rowIndex] &&
              (rowIndex !== columnIndex || value === 1)
          )
        )))
  );
}

export function buildDistributionChartOptions(
  chart: AiChatChartData
): Highcharts.Options {
  if (!isDistributionChart(chart))
    throw new Error("Unsupported distribution chart.");
  const common: Highcharts.Options = {
    chart: { animation: false },
    title: { text: chart.title },
    credits: { enabled: false },
  };
  if (chart.chartType === "boxplot") {
    const boxes = chart.boxes!;
    return {
      ...common,
      legend: { enabled: false },
      xAxis: { categories: boxes.map((box) => box.name) },
      yAxis: { title: { text: chart.yAxisTitle } },
      caption: {
        text: "Returned sample distribution; R-7 quartiles, 1.5-IQR observed whiskers.",
      },
      series: [
        {
          type: "boxplot",
          name: "Sample distribution",
          color: "#006b70",
          fillColor: "#d4ebea",
          data: boxes.map((box) => [
            box.low,
            box.q1,
            box.median,
            box.q3,
            box.high,
          ]),
        },
        {
          type: "scatter",
          name: "Outliers",
          color: "#cf293d",
          data: boxes.flatMap((box, index) =>
            box.outliers.map((value) => [index, value])
          ),
        },
      ],
    };
  }
  const matrix = chart.matrix!;
  if (chart.chartType === "polar") {
    const data = matrix.rows
      .map((name, index) => ({
        name,
        x: matrix.rowAngles?.[index] ?? (index * 360) / matrix.rows.length,
        y: matrix.values[index][0],
      }))
      .sort((first, second) => first.x - second.x);
    const spacing = Math.min(
      ...data.map((point, index) =>
        index === data.length - 1
          ? 360 + data[0].x - point.x
          : data[index + 1].x - point.x
      )
    );
    const minimum = Math.min(0, ...data.map((point) => point.y ?? 0));
    return {
      ...common,
      chart: { type: "column", polar: true, animation: false },
      pane: { startAngle: 0, endAngle: 360, size: "78%" },
      legend: { enabled: false },
      xAxis: {
        min: 0,
        max: 360,
        startOnTick: false,
        endOnTick: false,
        tickPositions: [0, 90, 180, 270],
        title: { text: undefined },
        labels: { format: "{value}\u00b0", style: { fontSize: "11px" } },
        gridLineWidth: 1,
      },
      yAxis: {
        min: minimum,
        title: { text: matrix.unit },
        gridLineInterpolation: "circle",
        labels: { style: { fontSize: "11px" } },
        ...(minimum < 0
          ? { plotLines: [{ value: 0, color: "#737373", width: 1, zIndex: 3 }] }
          : {}),
      },
      plotOptions: {
        column: {
          pointPlacement: "on",
          pointRange: spacing,
          groupPadding: 0.08,
          pointPadding: 0.05,
          borderWidth: 0,
          threshold: 0,
        },
      },
      tooltip: {
        useHTML: false,
        pointFormat: `<b>{point.name}</b><br/>Angle: {point.x}\u00b0<br/>Value: {point.y} ${matrix.unit.replace(/&/g, "&amp;")}`,
      },
      caption: {
        text: `${matrix.columns[0]}. ${matrix.rowAngles == null ? "Evenly spaced source order" : "Metadata angles"}; clockwise from 12 o'clock. Missing values remain gaps. Values retain their signs; not to-scale geometry.`,
      },
      series: [
        {
          type: "column",
          name: chart.title ?? "Signal",
          color: "#006b70",
          data,
        },
      ],
    };
  }
  const correlation = chart.chartType === "correlation";
  const timestampColumns =
    !correlation &&
    matrix.columns.every(
      (value) =>
        /^\d{4}-\d{2}-\d{2}[T ]/.test(value) &&
        Number.isFinite(Date.parse(value))
    );
  return {
    ...common,
    chart: {
      type: "heatmap",
      animation: false,
      style: { shapeRendering: "crispEdges" },
    },
    xAxis: {
      categories: matrix.columns,
      title: {
        text:
          timestampColumns && !/\bUTC\b/i.test(chart.xAxisTitle ?? "")
            ? `${chart.xAxisTitle ?? "Time"} (UTC)`
            : chart.xAxisTitle,
        style: { fontSize: "11px" },
      },
      tickPositioner: function () {
        const count = correlation
          ? matrix.columns.length
          : Math.min(
              matrix.columns.length,
              Math.max(2, Math.floor(this.len / 100))
            );
        return Array.from({ length: count }, (_, index) =>
          count === 1
            ? 0
            : Math.round((index * (matrix.columns.length - 1)) / (count - 1))
        );
      },
      labels: {
        rotation: 0,
        autoRotation: [],
        style: { fontSize: "11px", width: 80, textOverflow: "ellipsis" },
        formatter: function () {
          const value = String(this.value);
          return timestampColumns
            ? `${heatmapDate.format(Date.parse(value))}<br/>${heatmapTime.format(Date.parse(value))}`
            : value;
        },
      },
    },
    yAxis: {
      categories: matrix.rows,
      reversed: true,
      title: { text: chart.yAxisTitle, style: { fontSize: "11px" } },
      labels: {
        style: {
          fontSize: "11px",
          width: 112,
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      },
    },
    colorAxis: correlation
      ? {
          min: -1,
          max: 1,
          tickPositions: [-1, 0, 1],
          stops: [
            [0, "#2166ac"],
            [0.5, "#f7f7f7"],
            [1, "#b2182b"],
          ],
        }
      : { minColor: "#edf4f2", maxColor: "#006b70" },
    legend: { enabled: true },
    tooltip: {
      useHTML: false,
      formatter: function () {
        const column = matrix.columns[this.x];
        const time = timestampColumns
          ? new Date(Date.parse(column))
              .toISOString()
              .replace("T", " ")
              .replace("Z", " UTC")
          : column;
        const row = matrix.rows[this.y ?? 0];
        const value = matrix.values[this.y ?? 0][this.x];
        if (correlation)
          return `${column.replace(/&/g, "&amp;")}<br/><b>${row.replace(/&/g, "&amp;")}</b><br/>Pearson r: ${value!.toFixed(4)}<br/>${matrix.alignedSampleCount} aligned samples`;
        return `${time.replace(/&/g, "&amp;")}<br/><b>${row.replace(/&/g, "&amp;")}</b><br/>${value === null ? "Missing value" : `${value} ${matrix.unit.replace(/&/g, "&amp;")}`}`;
      },
    },
    caption: {
      text: correlation
        ? `Pearson r; ${matrix.alignedSampleCount} exact timestamps common to all signals. Returned resolution; no interpolation, lag estimation, significance or causal inference.`
        : "Gray cells: missing values. No interpolation or correlation implied.",
    },
    ...(correlation && matrix.columns.length >= 4
      ? {
          responsive: {
            rules: [
              {
                condition: { maxWidth: 600 },
                chartOptions: {
                  xAxis: {
                    labels: {
                      rotation: -60,
                      style: { width: 180, textOverflow: "ellipsis" },
                    },
                  },
                },
              },
            ],
          },
        }
      : {}),
    series: [
      {
        type: "heatmap",
        name: chart.title,
        nullColor: "#e1e1e1",
        borderWidth: 0,
        data: matrix.values.flatMap((row, rowIndex) =>
          row.map((value, columnIndex) => [columnIndex, rowIndex, value])
        ),
      },
    ],
  };
}
